// src/components/EditDiffsonaPage.ts
import { LitElement, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { getDiffsonaPrompts, updateDiffsonaPrompts } from '../client/ApiClient';

type Choice = { title: string; prompt: string };

@customElement('diffsona-edit')
export class EditDiffsonaPage extends LitElement {
  createRenderRoot() { return this; }

  @property({ type: String }) slug = '';

  @state() private loading = true;
  @state() private saving = false;
  @state() private error: string | null = null;

  // current values
  @state() private name = '';
  @state() private base = '';
  @state() private age = '';
  @state() private ethnicity = '';
  @state() private hair = '';
  @state() private face = '';
  @state() private body = '';

  // NEW: measurements
  @state() private bust = '';
  @state() private waist = '';
  @state() private hips = '';

  // curated (SFW) presets
  private presets = {
    base: <Choice[]>[
      { title: "Runway Fashion Model", prompt: "high-end runway model, striking pose, dramatic lighting" },
      { title: "Evening Gown", prompt: "luxurious evening gown, red carpet look, elegant posture" },
      { title: "Studio Portrait", prompt: "studio portrait, soft light, refined elegance" },
      { title: "Fitness Editorial", prompt: "athletic model, sportswear, confident pose" },
    ],
    age: <Choice[]>[
      { title: "Young adult", prompt: "young adult" },
      { title: "Early 20s", prompt: "in their early twenties" },
      { title: "Late 20s", prompt: "in their late twenties" },
      { title: "30s+", prompt: "mature adult" },
    ],
    ethnicity: <Choice[]>[
      { title: "Latina", prompt: "Latina" },
      { title: "East Asian", prompt: "East Asian" },
      { title: "South Asian", prompt: "South Asian" },
      { title: "Black", prompt: "Black" },
      { title: "White", prompt: "White" },
      { title: "Mixed", prompt: "mixed heritage" },
    ],
    hair: <Choice[]>[
      { title: "Long wavy brown", prompt: "long wavy brown hair" },
      { title: "Short black bob", prompt: "short black bob" },
      { title: "Curly red", prompt: "curly red hair" },
      { title: "Sleek blonde", prompt: "sleek blonde hair" },
    ],
    face: <Choice[]>[
      { title: "Natural", prompt: "soft features, minimal makeup" },
      { title: "Defined", prompt: "defined cheekbones, sharp jawline" },
      { title: "Freckles", prompt: "light freckles" },
      { title: "Glam", prompt: "glam makeup, glossy lips" },
    ],
    body: <Choice[]>[
      { title: "Athletic", prompt: "athletic build" },
      { title: "Slim", prompt: "slim build" },
      { title: "Curvy", prompt: "curvy build" },
      { title: "Plus", prompt: "plus-size" },
    ],

    // NEW: measurements
    bust: <Choice[]>[
      { title: "Small bust", prompt: "small bust" },
      { title: "Medium bust", prompt: "medium bust" },
      { title: "Full bust", prompt: "full bust" },
    ],
    waist: <Choice[]>[
      { title: "Narrow waist", prompt: "narrow waist" },
      { title: "Defined waist", prompt: "defined waist" },
      { title: "Natural waist", prompt: "natural waist" },
    ],
    hips: <Choice[]>[
      { title: "Slim hips", prompt: "slim hips" },
      { title: "Curvy hips", prompt: "curvy hips" },
      { title: "Wide hips", prompt: "wide hips" },
    ],
  };

  connectedCallback(): void {
    super.connectedCallback();
    if (!this.slug) {
      const m = window.location.pathname.match(/\/diffsona\/([^/]+)\/edit/);
      if (m) this.slug = decodeURIComponent(m[1]);
    }
    this.load();
  }

  private async load() {
    if (!this.slug) return;
    this.loading = true; this.error = null;
    try {
      const d = await getDiffsonaPrompts(this.slug);
      this.name = d.name ?? '';
      this.base = d.base_prompt ?? '';
      this.age = d.age_prompt ?? '';
      this.ethnicity = d.ethnicity_prompt ?? '';
      this.hair = d.hair_prompt ?? '';
      this.face = d.face_prompt ?? '';
      this.body = d.body_prompt ?? '';
      // NEW: pull from API
      this.bust  = d.bust_prompt  ?? '';
      this.waist = d.waist_prompt ?? '';
      this.hips  = d.hips_prompt  ?? '';
    } catch (e: any) {
      this.error = e?.message || 'Failed to load';
    } finally {
      this.loading = false;
    }
  }

  firstUpdated() {
    // Keep local state synced when prompt-inputs change
    this.addEventListener('change', (e: Event) => {
      const t = e.target as HTMLElement & { id?: string; value?: string };
      const id = (t as any)?.id; if (!id) return;
      const val = (t as any).value ?? '';
      switch (id) {
        case 'base': this.base = String(val); break;
        case 'age': this.age = String(val); break;
        case 'ethnicity': this.ethnicity = String(val); break;
        case 'hair': this.hair = String(val); break;
        case 'face': this.face = String(val); break;
        case 'body': this.body = String(val); break;
        case 'bust': this.bust = String(val); break;      // NEW
        case 'waist': this.waist = String(val); break;    // NEW
        case 'hips': this.hips = String(val); break;      // NEW
      }
    });
  }

  private rand<T>(arr: T[]): T { return arr[Math.floor(Math.random() * arr.length)] }
  private randomizeAll() {
    const picks = {
      base: this.rand(this.presets.base).prompt,
      age: this.rand(this.presets.age).prompt,
      ethnicity: this.rand(this.presets.ethnicity).prompt,
      hair: this.rand(this.presets.hair).prompt,
      face: this.rand(this.presets.face).prompt,
      body: this.rand(this.presets.body).prompt,
      bust: this.rand(this.presets.bust).prompt,    // NEW
      waist: this.rand(this.presets.waist).prompt,  // NEW
      hips: this.rand(this.presets.hips).prompt,    // NEW
    };
    (Object.keys(picks) as (keyof typeof picks)[]).forEach(k => {
      const el = this.querySelector<any>('#' + k);
      el?.setValue?.(picks[k]);
      (this as any)[k] = picks[k];
    });
  }

  private clearAll() {
    ['base','age','ethnicity','hair','face','body','bust','waist','hips'].forEach(id =>
      this.querySelector<any>('#'+id)?.setValue?.(''));
    this.base = this.age = this.ethnicity = this.hair = this.face = this.body = '';
    this.bust = this.waist = this.hips = '';
  }

  private async onSubmit(e: Event) {
    e.preventDefault();
    if (this.saving || this.loading) return;
    this.saving = true; this.error = null;
    try {
      const get = (id: string) => (this.querySelector<any>('#'+id)?.value ?? '').trim();
      const name = (this.querySelector<HTMLInputElement>('#name')?.value ?? this.name).trim();

      await updateDiffsonaPrompts(this.slug, {
        name,
        base: get('base'),
        age: get('age'),
        ethnicity: get('ethnicity'),
        hair: get('hair'),
        face: get('face'),
        body: get('body'),
        bust: get('bust'),
        waist: get('waist'),
        hips: get('hips'),
      });

      window.location.href = `/diffsona/${encodeURIComponent(this.slug)}`;
    } catch (e: any) {
      this.error = e?.message || 'Failed to save';
    } finally {
      this.saving = false;
    }
  }

  render() {
    const disabled = this.loading || this.saving;
    const chip = (label: string, value?: string) => value ? html`
      <div class="flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs text-slate-200">
        <span class="text-[10px] uppercase tracking-wide text-slate-400">${label}</span>
        <span class="max-w-[18rem] truncate">${value}</span>
      </div>` : null;

    return html`
      <main class="min-h-screen bg-gradient-to-b from-slate-900 via-slate-900 to-slate-950 text-slate-100">
        <div class="max-w-3xl mx-auto px-4 py-8 sm:py-12">
          <header class="text-center mb-8 sm:mb-10">
            <h1 class="text-3xl sm:text-4xl font-bold tracking-tight">Edit ${this.name ? html`<a href="/diffsona/${this.slug}">${this.name}</a>` : 'Diffsona'}</h1>
            <p class="mt-3 text-slate-300">Tune your character’s base attributes.</p>
          </header>

          ${this.loading ? html`
            <div class="space-y-4">
              ${Array.from({length:8}).map(() => html`
                <div class="rounded-xl border border-white/10 bg-slate-900/40 p-4">
                  <div class="h-5 w-40 bg-slate-800 rounded mb-3 animate-pulse"></div>
                  <div class="h-10 w-full bg-slate-800 rounded animate-pulse"></div>
                </div>
              `)}
            </div>
          ` : null}

          <form class="space-y-6 mt-4" @submit=${this.onSubmit}>
            <!-- Name -->
            <div class="${disabled ? 'opacity-70 pointer-events-none select-none' : ''}">
              <div class="text-base sm:text-lg font-semibold text-slate-200 mb-2">Character Name</div>
              <div class="relative">
                <input id="name" class="w-full rounded-xl bg-slate-900/60 border border-white/10 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-400/40"
                  .value=${this.name} placeholder="Character name" ?disabled=${disabled}/>
              </div>
            </div>

            <!-- Prompt Inputs -->
            <div class="space-y-6 ${disabled ? 'opacity-70 pointer-events-none select-none' : ''}">
              ${this.section('Base Prompt', 'base', this.presets.base, true, this.base)}
              ${this.section('Age Prompt', 'age', this.presets.age, true, this.age)}
              ${this.section('Ethnicity Prompt', 'ethnicity', this.presets.ethnicity, true, this.ethnicity)}
              ${this.section('Hair Prompt', 'hair', this.presets.hair, true, this.hair)}
              ${this.section('Face Prompt', 'face', this.presets.face, true, this.face)}
              ${this.section('Body Prompt', 'body', this.presets.body, true, this.body)}
              <!-- NEW -->
              ${this.section('Bust', 'bust', this.presets.bust, true, this.bust)}
              ${this.section('Waist', 'waist', this.presets.waist, true, this.waist)}
              ${this.section('Hips', 'hips', this.presets.hips, true, this.hips)}
            </div>

            <!-- Summary -->
            <section class="rounded-2xl border border-white/10 bg-white/5 p-3">
              <div class="text-sm text-slate-300 mb-2">Your base</div>
              <div class="flex flex-wrap gap-2">
                ${chip('Base', this.base)}
                ${chip('Age', this.age)}
                ${chip('Ethnicity', this.ethnicity)}
                ${chip('Hair', this.hair)}
                ${chip('Face', this.face)}
                ${chip('Body', this.body)}
                ${chip('Bust', this.bust)}
                ${chip('Waist', this.waist)}
                ${chip('Hips', this.hips)}
              </div>
            </section>

            ${this.error ? html`
              <p class="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">${this.error}</p>
            ` : null}

            <div class="flex flex-wrap items-center gap-2">
              <button type="button" class="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-sm"
                @click=${() => this.randomizeAll()} ?disabled=${disabled}>Randomize</button>

              <button type="button" class="rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 px-3 py-2 text-sm"
                @click=${() => this.clearAll()} ?disabled=${disabled}>Clear</button>

              <div class="ml-auto flex items-center gap-3">
                <a href="/diffsona/${encodeURIComponent(this.slug)}"
                   class="px-4 py-2 rounded-xl border border-white/10 bg-white/5 hover:bg-white/10 ${disabled ? 'pointer-events-none opacity-60' : ''}">
                  Cancel
                </a>
                <button type="submit"
                  class="inline-flex items-center gap-2 rounded-xl border border-indigo-400/30 bg-indigo-500/20 hover:bg-indigo-500/30 px-5 py-2.5 font-medium disabled:opacity-50"
                  ?disabled=${disabled}>
                  ${this.saving ? 'Saving…' : 'Save'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </main>
    `;
  }

  private section(label: string, id: string, values: Choice[], allowCustom: boolean, initial: string) {
    return html`
      <div class="space-y-2">
        <prompt-input
          .label=${label}
          id=${id}
          .values=${values}
          ?allow-custom=${allowCustom}
          .initialValue=${initial}
        ></prompt-input>
      </div>
    `;
  }
}

